import React from 'react';
import DataGridMultilinePopper from '@/components/table/DataGridComponents/DataGridMultilinePopper';
import { isProspectFieldVisible } from '@/features/config/helper';
import { FormFieldConfigSwitchCell } from './FormFieldConfigSwitchCell';
import { FormFieldConfigSourceCell } from './FormFieldConfigSourceCell';

const switchColumn = {
	width: 120,
	sortable: false,
	display: 'flex',
};

const textColumn = {
	flex: 2,
	minWidth: 200,
	sortable: false,
	editable: true,
	renderEditCell: (params) => <DataGridMultilinePopper {...params} newLine={false} />,
};

/**
 * Columns for the admin form field config list. The same settings and rules as Settings →
 * Client config (making a field required makes it visible; a field required by default can
 * can't be made not required unless its `optional` allows it), plus Description, which only
 * admins change.
 */
export const formFieldConfigColumns = [
	{
		field: 'label',
		headerName: 'Label',
		flex: 1.5,
		minWidth: 180,
		editable: true,
		hideable: false,
	},
	{
		field: 'name',
		headerName: 'Field name',
		flex: 1.5,
		minWidth: 180,
	},
	{
		...switchColumn,
		field: 'mandatory.client',
		headerName: 'Required (client)',
		valueGetter: (value, row) => row.mandatory?.client,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='required for clients'
				checked={row.mandatory?.client}
				disabled={row.mandatory?.client && !row.optional}
				disabledReason='This field is required by default and cannot be changed'
				getValues={(checked) => ({
					mandatory: { ...row.mandatory, client: checked },
					...(checked && { visible: { ...row.visible, create: true } }),
				})}
			/>
		),
	},
	{
		...switchColumn,
		field: 'mandatory.prospect',
		headerName: 'Required (prospect)',
		valueGetter: (value, row) => row.mandatory?.prospect,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='required for prospects'
				checked={row.mandatory?.prospect}
				disabled={row.mandatory?.prospect && !row.optional}
				disabledReason='This field is required by default and cannot be changed'
				getValues={(checked) => ({
					mandatory: { ...row.mandatory, prospect: checked },
					...(checked && {
						visible: { ...row.visible, prospect: { create: true, update: true } },
					}),
				})}
			/>
		),
	},
	{
		...switchColumn,
		field: 'visible.create',
		headerName: 'Visible (create)',
		valueGetter: (value, row) => row.visible?.create,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='visible when creating a client'
				checked={row.visible?.create}
				disabled={row.mandatory?.client}
				disabledReason='Required for clients, so always visible'
				getValues={(checked) => ({ visible: { ...row.visible, create: checked } })}
			/>
		),
	},
	// Prospect visibility is saved per view ({ create, update }); isProspectFieldVisible also
	// reads the older single boolean
	...['create', 'update'].map((view) => ({
		...switchColumn,
		field: `visible.prospect.${view}`,
		headerName: `Visible (prospect ${view})`,
		valueGetter: (value, row) => isProspectFieldVisible(row, view),
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label={`visible when ${view === 'create' ? 'creating' : 'updating'} a prospect`}
				checked={isProspectFieldVisible(row, view)}
				disabled={row.mandatory?.prospect}
				disabledReason='Required for prospects, so always visible'
				getValues={(checked) => ({
					visible: {
						...row.visible,
						prospect: {
							create: isProspectFieldVisible(row, 'create'),
							update: isProspectFieldVisible(row, 'update'),
							[view]: checked,
						},
					},
				})}
			/>
		),
	})),
	{
		...switchColumn,
		field: 'visible.update',
		headerName: 'Visible (update)',
		valueGetter: (value, row) => row.visible?.update,
		renderCell: ({ row }) => (
			<FormFieldConfigSwitchCell
				config={row}
				label='visible when updating a client'
				checked={row.visible?.update}
				getValues={(checked) => ({ visible: { ...row.visible, update: checked } })}
			/>
		),
	},
	{
		...textColumn,
		field: 'helperText',
		headerName: 'Helper text',
	},
	{
		...textColumn,
		field: 'description',
		headerName: 'Description',
	},
	{
		field: 'scope',
		headerName: 'Source',
		width: 160,
		display: 'flex',
		description: 'Where this row comes from: a broker group, a ledger or the default',
		renderCell: ({ row }) => <FormFieldConfigSourceCell config={row} />,
	},
];
